import {
  BuildingOfficeIcon,
  PencilSquareIcon,
  ShareIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";
import React from "react";
import styled from "styled-components";

import { Header, PageWrapper } from "src/shared/components";
import { LogoGradient } from "src/static/img";

const StyledHeader = styled(Header)`
  background: ${({ theme }) => theme.globalConstants.color.white};
`;

const ContentWrapper = styled(PageWrapper)`
  max-width: none;
  padding: 24px 32px 60px;

  ${({ theme }) => theme.mediaQueries.medium`
    padding: 24px 16px 64px;
  `}

  ${({ theme }) => theme.mediaQueries.tablet`
    padding-top: 0;
  `}
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
  width: 100%;
  color: ${({ theme }) => theme.globalConstants.color.greyPrimary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.globalConstants.color.borderPrimary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  font-size: 20px;
  font-weight: 400;
  line-height: 1;
`;

const Body = styled.p`
  margin: 0;
`;

const CardGrid = styled.div<{ columns?: number; gap?: number }>`
  display: grid;
  grid-template-columns: repeat(
    ${({ columns = 2 }) => columns},
    minmax(0, 1fr)
  );
  gap: ${({ gap = 16 }) => gap}px;

  ${({ theme }) => theme.mediaQueries.medium`
    grid-template-columns: 1fr;
    gap: 16px;
  `}
`;

const Card = styled.div`
  min-width: 0;
  overflow: hidden;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  background: ${({ theme }) => theme.globalConstants.color.white};
`;

const CardTitle = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 16px;
  font-weight: 700;
  line-height: 1.6;
`;

const TimelineSection = styled(Section)`
  gap: 8px;

  ${({ theme }) => theme.mediaQueries.medium`
    gap: 16px;
  `}
`;

const TimelineGrid = styled(CardGrid)`
  gap: 20px;
  margin-bottom: 48px;
`;

const TimelineCard = styled(Card)`
  height: 100%;
`;

const TimelineHeading = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  color: ${({ theme }) => theme.globalConstants.color.borderPrimary};
  font-weight: 700;

  svg {
    width: 24px;
    height: 24px;
    flex: 0 0 24px;
    stroke-width: 1.8;
  }
`;

const TimelineTitle = styled.span`
  line-height: 1.5;
`;

const MobileLogo = styled.img`
  display: none;

  ${({ theme }) => theme.mediaQueries.tablet`
    display: block;
    width: 39px;
    height: 37px;
    object-fit: contain;
  `}
`;

const timelineItems = [
  {
    title: "Publish Profile",
    body: "Fill out your directory card, choose your hacking pace, and share your goals.",
    icon: PencilSquareIcon,
  },
  {
    title: "Directory Launch",
    body: "A couple days after RSVP releases, profiles will launch and the directory will open for browsing.",
    icon: Squares2X2Icon,
  },
  {
    title: "Explore & Reach Out",
    body: "Browse pre-event and reach out directly via socials on their profile or through Slack.",
    icon: ShareIcon,
  },
  {
    title: "Meet & Build",
    body: "Connect in person at the event and build an incredible project together!",
    icon: BuildingOfficeIcon,
  },
];

const HackerDirectoryHome: React.FC = () => (
  <>
    <StyledHeader
      mobileLayout="stacked"
      title="Hacker Directory"
      subtitle="Discover, connect, and team up with fellow hackers before the event kicks off."
      actionButton={<MobileLogo src={LogoGradient} alt="Hack the North" />}
    />
    <ContentWrapper fullWidth>
      <Content>
        <Section>
          <Intro>
            <SectionTitle>What is the Hacker Directory?</SectionTitle>
            <Body>
              The Hacker Directory is a platform built specifically for hackers
              to showcase their profiles, skills, and hacking styles, and find
              teammates.
            </Body>
          </Intro>
          <CardGrid>
            <Card>
              <CardTitle>Who is it for?</CardTitle>
              <Body>
                Whether you are an individual looking for a team, a partial team
                searching for a specific missing skillset, or a fully formed
                group that wants to see what the community is building, this
                directory is for you.
              </Body>
            </Card>
            <Card>
              <CardTitle>Why use it?</CardTitle>
              <Body>
                Beyond team formation, it’s a space to discover inspiring peers,
                explore creative project ideas early, and establish a digital
                presence within the hacker community before stepping foot at the
                venue.
              </Body>
            </Card>
          </CardGrid>
        </Section>

        <TimelineSection>
          <SectionTitle>The Timeline</SectionTitle>
          <TimelineGrid columns={4}>
            {timelineItems.map(({ title, body, icon: Icon }, index) => (
              <TimelineCard key={title}>
                <TimelineHeading>
                  <TimelineTitle>
                    {index + 1}. {title}
                  </TimelineTitle>
                  <Icon aria-hidden="true" />
                </TimelineHeading>
                <Body>{body}</Body>
              </TimelineCard>
            ))}
          </TimelineGrid>
        </TimelineSection>
      </Content>
    </ContentWrapper>
  </>
);

export default HackerDirectoryHome;
